import config from "../config";
function authHeader() {
  let user = JSON.parse(localStorage.getItem("user"));
  if (user) {
    if (user.key) {
      return { Authorization: "Token  " + user.key };
    } else {
      return {};
    }
  } else {
    return {};
  }
}

const getPMSData = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/clinics/list-pms-offices/", // change to page = 0
    {
      method: "GET",
      headers: myHeaders,
    }
  );

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

// Does this user have a usable payment profile — and what is on it?
//
// Replaces GET /payment/api/user-customer-status/, which answered from
// `billinginformation_set.first()` (the LOWEST pk) and reported "no profile"
// whenever THAT row's customer_id was blank. The checkout believed it and POSTed
// create-platform-customer again, which created another row — the loop that
// minted the duplicate Helcim customers behind the "card declined" reports of
// 2026-09-16. This answers from the canonical row, and normalises on the way
// past, so asking the question also repairs it.
//
// Returns { has_profile, customer_id, has_customer_code, rows, billing } and
// keeps `status` for callers that still branch on the old shape.
const checkUserStatus = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(config.apiGateway.URL + "/usage/billing-profile/", {
    method: "GET",
    headers: myHeaders,
  });

  const response = await fetch(req);
  let json = {};
  try {
    json = await response.json();
  } catch (e) {
    json = {};
  }
  // `status` was the old endpoint's boolean answer; keep it so existing callers
  // read the same truth from the better source.
  json.status = Boolean(json.has_profile);
  return json;
};

// Which onboarding screens THIS user was given at registration (the CRM Settings
// switches, frozen at signup). null on any failure → the wizard behaves as before.
const getOnboardingPlan = async () => {
  try {
    const req = new Request(config.apiGateway.URL + "/clinics/onboarding-plan/", {
      method: "GET",
      headers: Object.assign({ "Content-Type": "application/json" }, authHeader()),
    });
    const response = await fetch(req);
    if (!response.ok) return null;
    return await response.json();
  } catch (e) {
    return null;
  }
};

const skipOnBoarding = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/clinics/skip-onboarding-step/", // change to page = 0
    {
      method: "GET",
      headers: myHeaders,
    }
  );

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

const addNewOfficeQuestion = async (answer) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(config.apiGateway.URL + "/clinics/add-another-office/?more_offices=" + answer, {
    method: "GET",
    headers: myHeaders,
  });

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

const LicencesList = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/accounts/list-subscription-tier/?page=0", // change to page = 0
    {
      method: "GET",
      headers: myHeaders,
    }
  );

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

// The single subscription tier that covers an office (the office's active tier,
// or the default Starter tier during onboarding). Invited users all share it.
// Did THIS user's purchase for THIS office get paid? The card attach answers 201
// the moment the card is stored; the charge runs just after, off the request
// thread, so the purchase screen polls this. Distinct from officeSubscriptionTier,
// which asks what plan the office resolves to for metering.
const officeSubscriptionStatus = async (officeId) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/usage/offices/" + officeId + "/subscription-status/",
    { method: "GET", headers: myHeaders }
  );
  const response = await fetch(req);
  let json = {};
  try {
    json = await response.json();
  } catch (e) {
    json = {};
  }
  json.status = response.status;
  return json;
};

const officeSubscriptionTier = async (officeId) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/clinics/office-subscription-tier/?office_id=" + officeId,
    {
      method: "GET",
      headers: myHeaders,
    }
  );
  const response = await fetch(req);
  return await response.json();
};

// SaaS plan picker catalog: plan families (Starter/Pro/Concierge) each with a
// monthly + annual variant, PRICED FOR ONE OFFICE when officeId is given — its
// offers applied, plus `special_families` (special plans offered to it).
const getSubscriptionCatalog = async (officeId) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const qs = officeId ? "?office_id=" + encodeURIComponent(officeId) : "";
  const req = new Request(config.apiGateway.URL + "/usage/subscription-catalog/" + qs, {
    method: "GET",
    headers: myHeaders,
  });
  const response = await fetch(req);
  const json = await response.json();
  json.status = response.status;
  return json;
};

// The shared JSON-with-status shape these billing calls return (no {json} wrapper).
const billingRequest = async (path, { method = "GET", body } = {}) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(config.apiGateway.URL + path, {
    method,
    headers: myHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });
  const response = await fetch(req);
  let json = {};
  try {
    json = await response.json();
  } catch (e) {
    json = {};
  }
  if (Array.isArray(json)) json = { results: json };
  json.status = response.status;
  return json;
};

// Every office of the selected office's practice this user may see plans for,
// with its plan state — plus whether they can buy and have a saved card.
const getPracticePlans = (officeId) =>
  billingRequest("/usage/practice-plans/?office_id=" + encodeURIComponent(officeId));

// Buy a plan for one or more offices: items = [{office_id, subscription_tier_id}],
// payWith = "new_card" (the card step follows) | "saved_card" (charged now).
const startCheckout = (items, payWith = "new_card") =>
  billingRequest("/usage/checkout/", { method: "POST", body: { items, pay_with: payWith } });

// Whether each office's latest purchase has been paid.
const getCheckoutStatus = (officeIds) =>
  billingRequest("/usage/checkout-status/?office_ids=" + (officeIds || []).join(","));

// Subscribe the office to a directly-chosen tier (flat office subscription).
const subscribeOfficeToTier = async (officeId, tierId) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/usage/offices/" + officeId + "/subscribe/",
    {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ subscription_tier_id: tierId }),
    }
  );
  const response = await fetch(req);
  const json = await response.json();
  json.status = response.status;
  return json;
};

const invitedUserList = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/accounts/list-user-invites/?page=0&branch_id=" + localStorage.getItem("selectedOffice"), // change to page = 0
    {
      method: "GET",
      headers: myHeaders,
    }
  );

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

const getOnboardingSteps = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/accounts/check-user-onboarding/", // change to page = 0
    {
      method: "GET",
      headers: myHeaders,
    }
  );

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

const getAllInvitedUser = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(
    config.apiGateway.URL + "/accounts/list-user-invites/?page=0", // change to page = 0
    {
      method: "GET",
      headers: myHeaders,
    }
  );

  const response = await fetch(req);
  const json = await response.json();
  return json;
};

const checkIsMultiple = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const req = new Request(config.apiGateway.URL + "/clinics/is-multiple-office/", {
    method: "GET",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const PassResetConfirm = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" });

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/accounts/password-reset-confirm/" + data.uid + "/" + data.token + "/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const forgotPass = async (data) => {
  const myHeaders = Object.assign(
    { "Content-Type": "application/json" }
    //authHeader()
  );

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/accounts/account-password-reset/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const inviteMembership = async (data) => {
  const myHeaders = Object.assign(authHeader());

  const req = new Request(config.apiGateway.URL + "/accounts/invite-membership/", {
    body: data,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

// The billing details already on file, from the payment package's own
// GET /payment/api/get-create-customer/. It answers with the BillingInformation
// row (first_name, last_name, address1, city, state, postalCode, country...),
// which is exactly what the card step needs to inherit. It 500s for the few
// users who hold more than one payment client (a bare .get() upstream), so a
// caller treats anything but a clean 200 as "nothing on file" and asks.
const getBillingDetails = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(config.apiGateway.URL + "/payment/api/get-create-customer/", {
    method: "GET",
    headers: myHeaders,
  });
  const response = await fetch(req);
  let json = {};
  try {
    json = await response.json();
  } catch (e) {
    json = {};
  }
  json.status = response.status;
  return json;
};

const createPlatformCustomer = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/payment/api/create-platform-customer/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const setMultipleOffice = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/clinics/multiple-offices/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const inviteUser = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/accounts/create-invite/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const SignUp = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/accounts/user-sign-up/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  console.log(response);
  var json = await response.json();
  json.status = response.status;
  return json;
};

// Public (no session yet): the email an invite was sent to, for the sign-up
// prefill. 404 for anything that isn't an open invite — the form treats that as
// "nothing to prefill", never as an error.
const lookupInvite = async (token) => {
  const req = new Request(
    config.apiGateway.URL + "/crm/invites/lookup/?token=" + encodeURIComponent(token),
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const response = await fetch(req);
  let json = {};
  try {
    json = await response.json();
  } catch (e) {
    json = {};
  }
  json.status = response.status;
  return json;
};

const createOfficePractice = async (data) => {
  const myHeaders = Object.assign(authHeader());

  const req = new Request(config.apiGateway.URL + "/clinics/create-office-practice/", {
    body: data,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

// What the onboarding wizard may pre-fill for the logged-in user: the lead
// they came from (practice name, phone, email, address, PMS…), else the
// invite's own prefill. {} when there is nothing. Never fails the wizard.
const getOnboardingPrefill = async () => {
  try {
    const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
    const req = new Request(config.apiGateway.URL + "/crm/onboarding-prefill/", {
      method: "GET",
      headers: myHeaders,
    });
    const response = await fetch(req);
    if (!response.ok) return {};
    return (await response.json()) || {};
  } catch (e) {
    return {};
  }
};

const createPractice = async (data) => {
  const myHeaders = Object.assign(authHeader());

  const req = new Request(config.apiGateway.URL + "/clinics/create-practice/", {
    body: data,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const updateOffice = async (data, office_id) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);
  const req = new Request(config.apiGateway.URL + "/clinics/update-office-flow/" + office_id + "/", {
    body: raw,
    method: "PUT",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const createOffice = async (data, id) => {
  const myHeaders = Object.assign(authHeader());

  const req = new Request(config.apiGateway.URL + "/clinics/create-office/?practice_id=" + id, {
    body: data,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const updateOfficePractice = async (data, id) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/clinics/update-office-practice/" + id + "/", {
    body: raw,
    method: "PUT",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const updatePractice = async (data, id) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/clinics/update-practice/" + id + "/", {
    body: raw,
    method: "PUT",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const Login = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const raw = JSON.stringify(data);

  const req = new Request(config.apiGateway.URL + "/accounts/account-login/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const deleteInvitedUser = async (id) => {
  const myHeaders = Object.assign(authHeader());
  var url = "";
  url = config.apiGateway.URL + "/accounts/delete-user-invite/" + id + "/";

  const req = new Request(url, {
    method: "DELETE",
    headers: myHeaders,
  });

  const response = await fetch(req);

  if (response.status > 250) {
    const json = await response.json();
    const res = {
      json: json,
      status: response.status,
      message: response.message,
    };

    return res;
  } else {
    const res = {
      json: { message: "succssfull" },
      status: 204,
      message: "Service deleted successfully",
    };

    return res;
  }
};

const getHelcimToken = async (data) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const req = new Request(config.apiGateway.URL + "/accounts/get-helcim-js-token/", {
    method: "GET",
    headers: myHeaders,
  });

  const response = await fetch(req);
  const json = await response.json();
  json.status = response.status;

  return json;
};

const helcimPayMulti = async (cardtoken) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  var payload = {
    payment_method_token: cardtoken,
  };

  const raw = JSON.stringify(payload);

  const req = new Request(config.apiGateway.URL + "/accounts/attach-payment-method/", {
    body: raw,
    method: "POST",
    headers: myHeaders,
  });

  const response = await fetch(req);
  const json = await response.json();
  json.status = response.status;

  return json;
};

const generateAccountBank = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(config.apiGateway.URL + "/payment/api/generate-add-bank-link/?business_id=" + localStorage.getItem("selectedOffice"), {
    method: "GET",
    headers: myHeaders,
  });

  const response = await fetch(req);

  var json = await response.json();

  json.status = response.status;
  return json;
};

const officeprofile = async () => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());

  const req = new Request(config.apiGateway.URL + "/clinics/get-office-profile/" + localStorage.getItem("selectedOffice") + "/", {
    method: "GET",
    headers: myHeaders,
  });

  const response = await fetch(req);
  const json = await response.json();
  json.status = response.status;
  return json;
};

const getTrialConfig = async (officeId) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  // With an office the response also carries {trial: {used, quota, ...}} — the
  // live counter the banner renders. Without one it is config-only (onboarding
  // asks before an office is selected).
  const qs = officeId ? `?office_id=${encodeURIComponent(officeId)}` : "";
  const req = new Request(config.apiGateway.URL + "/usage/trial/config/" + qs, {
    method: "GET",
    headers: myHeaders,
  });
  const response = await fetch(req);
  const json = await response.json();
  json.status = response.status;
  return json;
};

const startTrial = async (officeId) => {
  const myHeaders = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const req = new Request(config.apiGateway.URL + "/usage/trial/start/", {
    method: "POST",
    headers: myHeaders,
    body: JSON.stringify({ office_id: officeId }),
  });
  const response = await fetch(req);
  const json = await response.json();
  // The body's own `status` ("started" / "already_active" / "already_used" /
  // "has_subscription" / "disabled") is what the button's messages key on;
  // keep it before the HTTP code overwrites the key, as every controller here does.
  json.result_status = json.status;
  json.status = response.status;
  return json;
};

const setOfficeSikka = async (offices, skip) => {
  const myHeaders = Object.assign(authHeader(), { "Content-Type": "application/json" });
  const req = new Request(config.apiGateway.URL + "/clinics/set-office-sikka/", {
    body: JSON.stringify({ offices: offices || [], skip: !!skip }),
    method: "POST",
    headers: myHeaders,
  });
  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

const getOfficeSikkaStatus = async (officeId) => {
  const myHeaders = Object.assign(authHeader(), { "Content-Type": "application/json" });
  const req = new Request(
    config.apiGateway.URL + "/clinics/office-sikka-status/?office_id=" + officeId,
    { method: "GET", headers: myHeaders }
  );
  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

// Advance onboarding past the portal_credentials step (after saving, or on skip).
const completePortalCredentialsStep = async () => {
  const myHeaders = Object.assign(authHeader(), { "Content-Type": "application/json" });
  const req = new Request(config.apiGateway.URL + "/clinics/complete-portal-credentials-step/", {
    method: "POST",
    headers: myHeaders,
  });
  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

// Advance onboarding past the procedure_code_defaults step (after saving overrides,
// or on skip). The per-carrier overrides themselves are saved via the main
// controller's setOfficeBreakdownProcedureCodes / setOfficeProcedureCategories.
const completeProcedureCodeDefaultsStep = async () => {
  const myHeaders = Object.assign(authHeader(), { "Content-Type": "application/json" });
  const req = new Request(config.apiGateway.URL + "/clinics/complete-procedure-code-defaults-step/", {
    method: "POST",
    headers: myHeaders,
  });
  const response = await fetch(req);
  var json = await response.json();
  json.status = response.status;
  return json;
};

export const controller = {
  setOfficeSikka,
  getOfficeSikkaStatus,
  completePortalCredentialsStep,
  completeProcedureCodeDefaultsStep,
  Login,
  SignUp,
  lookupInvite,
  getTrialConfig,
  startTrial,

  createOfficePractice,
  updateOfficePractice,

  createPractice,
  getOnboardingPrefill,
  updatePractice,

  createOffice,
  updateOffice,

  LicencesList,
  officeSubscriptionTier,
  getSubscriptionCatalog,
  subscribeOfficeToTier,
  getPracticePlans,
  startCheckout,
  getCheckoutStatus,
  inviteUser,
  invitedUserList,
  deleteInvitedUser,
  getAllInvitedUser,
  getOnboardingSteps,
  setMultipleOffice,
  skipOnBoarding,
  getOnboardingPlan,

  addNewOfficeQuestion,

  checkUserStatus,
  createPlatformCustomer,
  getBillingDetails,
  officeSubscriptionStatus,
  getHelcimToken,
  inviteMembership,
  helcimPayMulti,
  forgotPass,
  PassResetConfirm,
  checkIsMultiple,

  generateAccountBank,

  getPMSData,

  officeprofile,
};
